<template>
  <aside
    class="sidebar"
    :class="{
      'sidebar--collapsed': collapsed,
      'sidebar--hover': isHovering && collapsed
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="sidebar__logo">
      <a href="" class="sidebar__logo-link">
        <BrandLogo :variant="(!collapsed || isHovering) ? 'wordmark' : 'mark'" tone="dark" />
      </a>
    </div>
    <nav class="sidebar__nav">
      <div
        v-for="item in items"
        :key="item.menuWebCode"
        class="sidebar__menu-group"
      >
        <!-- 직행 그룹(대시보드/AI 에이전트 관리 — 그룹 urlPath 보유): 하위메뉴 없이 상위 클릭으로 바로 이동 -->
        <button
          class="sidebar__menu-item"
          :class="{ 'sidebar__menu-item--active': isDirectGroup(item) ? isDirectActive(item) : item.isExpanded }"
          @click="isDirectGroup(item) ? reToken($event, { urlPath: item.urlPath, name: item.name }) : subMenu(item)"
        >
          <i class="sidebar__menu-icon fa-solid" :class="getMenuIcon(item.name)"></i>
          <span v-show="!collapsed || isHovering" class="sidebar__menu-label">{{ item.name }}</span>
          <i
            v-show="(!collapsed || isHovering) && !isDirectGroup(item)"
            class="sidebar__menu-arrow fa-solid"
            :class="item.isExpanded ? 'fa-angle-up' : 'fa-angle-down'"
          ></i>
        </button>
        <div
          v-if="!isDirectGroup(item) && item.isExpanded && (!collapsed || isHovering)"
          class="sidebar__submenu"
        >
          <a
            v-for="child in item.childMenuList"
            :key="child.menuWebCode"
            class="sidebar__submenu-item"
            :class="{
              'sidebar__submenu-item--active': isActiveChild(child),
              'sidebar__submenu-item--with-badge': submenuBadgeCount(child) > 0
            }"
            href="#"
            @click="reToken($event, child)"
          >
            <span>{{ child.name }}</span>
            <!-- 대기 건수 배지 — 0 이면 그리지 않는다(SURINP-342 D12) -->
            <span v-if="submenuBadgeCount(child) > 0" class="sidebar__submenu-badge">{{ submenuBadgeCount(child) }}</span>
          </a>
        </div>
      </div>
    </nav>
    <div v-show="!collapsed || isHovering" class="sidebar__user">
      <div class="sidebar__user-avatar">{{ userInitial }}</div>
      <div class="sidebar__user-info">
        <span class="sidebar__user-name">{{ userName }}</span>
        <span class="sidebar__user-role">{{ userRole }}</span>
      </div>
    </div>
  </aside>
</template>

<script>
import store from '@/store'
import axios from 'axios'
import { getNewToken } from '@/api/domain/common'
import jwtDecode from 'jwt-decode'
import { mapState as mapPiniaState } from 'pinia'
import { useTabStore } from '@/stores/tabs'
import { clearConversation } from '@/stores/assistantStorage'
import { useMenuStore, resolveMenuPath } from '@/stores/menu'
import { usePartnershipBadgeStore, PARTNERSHIP_BADGE_MENU_CODE } from '@/stores/partnershipBadge'
import BrandLogo from '@/components/common/BrandLogo.vue'
import { codeLabel } from '@/constants/codeLabel'

axios.defaults.withCredentials = true

/**
 * @component HeaderMenu
 * @description PC 전용 좌측 사이드바 내비게이션 컴포넌트.
 * menuStore 에서 메뉴 트리를 조회하여 아코디언 방식으로 렌더링합니다.
 * 접힘/펼침(collapsed) 상태를 지원하며, hover 시 임시 확장됩니다.
 * 탭 전환 시 활성 탭 경로를 watch 하여 해당 메뉴 그룹을 자동 펼치고 sub-menu 를 하이라이트합니다.
 * 메뉴 클릭 시 토큰을 갱신(reToken)한 후 탭을 열고 해당 경로로 이동합니다.
 */
export default {
  components: { BrandLogo },
  // ── Props ──
  props: {
    /**
     * 사이드바 접힘 여부 — true이면 64px로 축소
     * @type {boolean}
     */
    collapsed: { type: Boolean, default: false }
  },
  emits: ['toggle-collapse'],
  setup() {
    const tabStore = useTabStore()
    const menuStore = useMenuStore()
    const partnershipBadgeStore = usePartnershipBadgeStore()
    return { tabStore, menuStore, partnershipBadgeStore }
  },
  // ── 반응형 데이터 ──
  data() {
    return {
      loginId: '',
      /** 마우스 hover 여부 — 접힌 상태에서 임시 확장에 사용 */
      isHovering: false,
      /** 메뉴 이름 → Font Awesome 아이콘 클래스 매핑 */
      menuIconMap: {
        대시보드: 'fa-chart-pie',
        상담: 'fa-headset',
        접수: 'fa-clipboard-list',
        접수관리: 'fa-screwdriver-wrench',
        인프라관리: 'fa-server',
        시스템관리: 'fa-gear',
        고객사관리: 'fa-building',
        정산관리: 'fa-calculator',
        '리포트/통계': 'fa-chart-line',
        'AI 에이전트 관리': 'fa-robot',
        '고객사·협력사 신청': 'fa-handshake',
        Dashboard: 'fa-chart-pie',
        Consultation: 'fa-headset',
        Reception: 'fa-clipboard-list',
        Repair: 'fa-screwdriver-wrench',
        Infrastructure: 'fa-server',
        System: 'fa-gear',
        Customer: 'fa-building',
        Settlement: 'fa-calculator',
        'Reports/Statistics': 'fa-chart-line',
        'AI Agent Management': 'fa-robot'
      }
    }
  },
  // ── 계산된 속성 ──
  computed: {
    ...mapPiniaState(useTabStore, ['activeTabPath']),
    /**
     * menuStore 의 메뉴 트리를 템플릿에서 사용할 수 있도록 노출합니다.
     * @returns {Array} 메뉴 그룹 배열 (각 그룹에 isExpanded, childMenuList 포함)
     */
    items() {
      return this.menuStore.tree
    },
    /**
     * 고객사·협력사 신청 메뉴가 트리에 머지됐는지 여부를 반환합니다.
     * 배지 조회를 이 메뉴가 보이는 사용자에게만 돌리기 위한 게이트입니다(등급 판정 중복 방지).
     * @returns {boolean}
     */
    hasPartnershipMenu() {
      return this.menuStore.tree.some((g) =>
        (g.childMenuList || []).some((c) => c.menuWebCode === PARTNERSHIP_BADGE_MENU_CODE)
      )
    },
    /**
     * Vuex auth 스토어에서 현재 로그인 사용자 이름을 반환합니다.
     */
    userName() {
      const user = this.$store.state.auth.user
      return user && user.id ? (user.id.userName || user.id.loginId || '') : ''
    },
    /**
     * 사용자 이름의 첫 글자(대문자)를 반환합니다. 이름이 없으면 'U'를 반환합니다.
     */
    userInitial() {
      const name = this.userName
      return name ? name.charAt(0).toUpperCase() : 'U'
    },
    /**
     * 사용자 유형(userType)을 로케일 역할명으로 변환하여 반환합니다(모르는 유형은 원문).
     */
    userRole() {
      const user = this.$store.state.auth.user
      if (!user || !user.id) return ''
      // ADMIN·PARTNER 만 바꾸고 OPERATOR 등은 원문이 보이던 것 — CodeLabel 한 곳에서 바꾼다 (SURINP-679)
      return codeLabel('userType', user.id.userType)
    }
  },
  mounted () {
    const authToken = sessionStorage.getItem('authToken')

    if (authToken !== null) {
      this.loginId = store.state.auth.user.id.loginId
      this.menuStore.loadMenu().then(() => {
        // 초기 로드 시 현재 활성 탭 경로에 해당하는 그룹을 자동 펼침
        if (this.activeTabPath) {
          this.menuStore.setExpandedByPath(this.activeTabPath)
        }
        // 대기 건수 배지는 폴링하지 않는다 — 메뉴 로드 직후 1회만 조회한다(SURINP-342 D12)
        if (this.hasPartnershipMenu) {
          this.partnershipBadgeStore.fetchCount()
        }
      })
    }
  },
  // ── watch ──
  watch: {
    /**
     * 탭 전환 시 해당 경로의 메뉴 그룹을 자동 펼침하고 sub-menu 를 하이라이트합니다.
     */
    activeTabPath(newPath) {
      if (newPath) {
        this.menuStore.setExpandedByPath(newPath)
      }
    }
  },
  // ── 메서드 ──
  methods: {
    /**
     * 사이드바가 접힌 상태에서 마우스가 올라오면 임시 확장 상태를 활성화합니다.
     */
    onMouseEnter() {
      if (this.collapsed) {
        this.isHovering = true
      }
    },
    /**
     * 마우스가 사이드바를 벗어나면 임시 확장 상태를 해제합니다.
     */
    onMouseLeave() {
      this.isHovering = false
    },
    /**
     * 메뉴 이름에 대응하는 Font Awesome 아이콘 클래스를 반환합니다.
     * @param {string} name - 메뉴 이름
     * @returns {string} Font Awesome 클래스명
     */
    getMenuIcon(name) {
      return this.menuIconMap[name] || 'fa-folder'
    },
    /**
     * 상위 메뉴 버튼 클릭 시 서브메뉴 펼침/접힘 상태를 토글합니다.
     * menuStore 를 통해 반응성 있게 관리됩니다.
     * @param {Object} item - 메뉴 항목 객체
     */
    subMenu(item) {
      this.menuStore.toggleGroup(item.menuWebCode)
    },
    /**
     * 하위메뉴 없이 상위 클릭으로 바로 이동하는 그룹인지 판정합니다.
     * BE 메뉴 마스터('대시보드')·정적 메뉴('AI 에이전트 관리')가 그룹 레벨 urlPath 로 표시합니다.
     * @param {Object} item - 메뉴 그룹
     * @returns {boolean}
     */
    isDirectGroup(item) {
      return !!item.urlPath
    },
    /**
     * 직행 그룹이 현재 활성 탭 경로와 일치하는지 판정합니다(하이라이트용).
     * @param {Object} item - 직행 메뉴 그룹 (urlPath 포함)
     * @returns {boolean}
     */
    isDirectActive(item) {
      const activePath = resolveMenuPath(this.activeTabPath)
      return !!activePath && item.urlPath === activePath
    },
    /**
     * 하위 메뉴 항목이 현재 활성 탭 경로와 일치하는지 판정합니다.
     * 파라미터 상세 라우트(위젯 카드/행 클릭 이동 포함)는 소속 목록 메뉴로 매핑해 판정합니다.
     * @param {Object} child - 하위 메뉴 항목 (urlPath 포함)
     * @returns {boolean}
     */
    isActiveChild(child) {
      const activePath = resolveMenuPath(this.activeTabPath)
      return !!activePath && child.urlPath === activePath
    },
    /**
     * 하위 메뉴에 표시할 배지 숫자를 반환합니다. 배지 대상이 아니면 0 입니다.
     * @param {Object} child - 하위 메뉴 항목
     * @returns {number}
     */
    submenuBadgeCount(child) {
      if (child.menuWebCode !== PARTNERSHIP_BADGE_MENU_CODE) return 0
      return this.partnershipBadgeStore.count
    },
    /**
     * 하위 메뉴 클릭 시 JWT 토큰을 갱신하고 해당 경로의 탭을 엽니다.
     * 403 에러 발생 시 세션을 초기화하고 로그인 페이지로 이동합니다.
     * @param {Event} event - 클릭 이벤트
     * @param {Object} childMenuList - 클릭한 하위 메뉴 항목 (urlPath, name 포함)
     */
    async reToken(event, childMenuList) {
      const vm = this
      const urlPath = childMenuList.urlPath
      const urlPathName = window.location.pathname

      event.preventDefault()

      // reFresh token api
      getNewToken('post')
        .then((res) => {
          const authToken = res.data
          const decodedToken = jwtDecode(authToken)
          const userType = decodedToken.userType
          const userGroupId = decodedToken.userGroupId
          const partnerId = this.$store.state.auth.user.id.partnerId || ''
          this.$store.commit('user', { loginId: this.loginId, authToken, userGroupId, userType, partnerId })
          sessionStorage.setItem('authToken', authToken)

          const tabStore = useTabStore()
          const route = this.$router.resolve(urlPath)
          const opened = tabStore.openTab(route, childMenuList.name)
          if (opened !== false) {
            this.$router.push(urlPath)
          }
        })
        .catch(error => {
          window.__APP_LOG__(error)
          if (error.response?.status === 403) {
            this.$store.commit('user', {})
            sessionStorage.removeItem('authToken')
            const tabStore = useTabStore()
            tabStore.clearAll()
            clearConversation() // 어시스턴트 대화 저장분(AA-143)
            window.appAlert(this.$t('message.session'))
            // SURINP-191 (2026-04-29): popupPaths 배열 + popupPaths.includes() 분기 + window.close() 호출 모두 제거.
            // 본래 별창 register popup 이 부모 세션 만료 시 자체 close() 하기 위한 메커니즘이었으나,
            // S7 정리로 모든 register popup 이 ModalPopup 으로 흡수되어 popup window 자체가 더 이상 존재하지 않음.
            if (urlPathName.startsWith('/mobile/staff-team')) {
              vm.$router.push({ path: '/mobile/staff-team/login' })
            } else {
              vm.$router.push({ path: '/login' })
            }
          }
        })
    }
  }
}
</script>
<style scoped>
/* --- 사이드바 컨테이너 (260px 기본, 접힘 시 64px) --- */
.sidebar {
  width: var(--sidebar-width, 260px);
  height: 100vh;
  background: var(--color-secondary, #1A2332);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width var(--transition-normal, 0.2s ease);
  z-index: var(--z-sidebar, 100);
}

.sidebar::-webkit-scrollbar {
  width: 4px;
}

.sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.sidebar--collapsed {
  width: var(--sidebar-collapsed-width, 64px);
}

.sidebar--collapsed.sidebar--hover {
  width: var(--sidebar-width, 260px);
  position: absolute;
  z-index: 200;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
}

/* --- 로고 영역 --- */
.sidebar__logo {
  height: var(--space-16, 64px);
  display: flex;
  align-items: center;
  padding: 0 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar__logo-link {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #fff;
  --brand-logo-size: 23px;
}

/* --- 내비게이션 메뉴 목록 --- */
.sidebar__nav {
  flex: 1;
  padding: 10px;
}

.sidebar__menu-group {
  margin-bottom: 2px;
}

.sidebar__menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  font-weight: var(--font-weight-medium, 500);
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s ease), color var(--transition-fast, 0.15s ease);
  text-align: left;
  white-space: nowrap;
  position: relative;
}

.sidebar__menu-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
}

.sidebar__menu-item--active {
  background: rgba(59, 89, 152, 0.3);
  color: #fff;
}

.sidebar__menu-item--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  background: var(--color-primary-500, #3B5998);
  border-radius: 0 2px 2px 0;
}

.sidebar__menu-icon {
  width: 20px;
  text-align: center;
  font-size: 18px;
  flex-shrink: 0;
}

.sidebar__menu-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__menu-arrow {
  font-size: 12px;
  opacity: 0.8;
  flex-shrink: 0;
  transition: transform var(--transition-fast, 0.15s ease);
}

/* --- 서브메뉴 (들여쓰기 적용) --- */
.sidebar__submenu {
  padding: 6px 0 8px 32px;
}

.sidebar__submenu-item {
  display: block;
  padding: 9px 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  border-radius: 6px;
  transition: color var(--transition-fast, 0.15s ease), background var(--transition-fast, 0.15s ease);
  cursor: pointer;
}

.sidebar__submenu-item:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

/* --- 배지가 붙은 항목만 좌우 정렬로 전환 (다른 서브메뉴의 레이아웃은 건드리지 않는다) --- */
.sidebar__submenu-item--with-badge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sidebar__submenu-badge {
  flex-shrink: 0;
  min-width: 18px;
  padding: 0 6px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-danger, #C03A3A);
  color: #fff;
  font-size: 11px;
  font-weight: var(--font-weight-bold, 700);
  line-height: 18px;
  text-align: center;
}

/* --- 하단 사용자 프로필 영역 --- */
.sidebar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.sidebar__user-avatar {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-primary-500, #3B5998);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: var(--font-weight-semibold, 600);
  flex-shrink: 0;
}

.sidebar__user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sidebar__user-name {
  font-size: 15px;
  font-weight: var(--font-weight-semibold, 600);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

/* --- 태블릿/모바일 반응형: 사이드바 숨김 --- */
@media (max-width: 1279px) {
  .sidebar {
    display: none;
  }
}
</style>
